import React, { useState, useEffect, createContext, useContext, ReactNode } from 'react'
import { User } from '@/types'
import { authService } from '@/services/authService'

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>
  register: (data: {
    email: string
    password: string
    firstName: string
    lastName: string
    phoneNumber?: string
  }) => Promise<{ success: boolean; error?: string }>
  logout: () => Promise<void>
  signOutUser: () => Promise<void> // Alias pour la compatibilité
  updateUser: (userData: Partial<User>) => void
  isAuthenticated: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check if user is already authenticated
        if (authService.isAuthenticated()) {
          const currentUser = authService.getCurrentUser()
          if (currentUser) {
            // Refresh user data from API to get latest info
            const refreshResponse = await authService.refreshUserData()
            if (refreshResponse.success && refreshResponse.user) {
              setUser(refreshResponse.user)
            } else {
              setUser(currentUser)
            }
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
        // Clear potentially corrupted auth data
        localStorage.removeItem('authToken')
        localStorage.removeItem('userData')
        setUser(null)
      } finally {
        setLoading(false)
      }
    }

    initializeAuth()
  }, [])

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string }> => {
    try {
      setLoading(true)
      const response = await authService.login({ email, password })

      if (response.success && response.user) {
        setUser(response.user)
        return { success: true }
      } else {
        return {
          success: false,
          error: response.error?.message || 'Erreur lors de la connexion'
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Erreur inattendue lors de la connexion'
      }
    } finally {
      setLoading(false)
    }
  }

  const register = async (data: {
    email: string
    password: string
    firstName: string
    lastName: string
    phoneNumber?: string
  }): Promise<{ success: boolean; error?: string }> => {
    try {
      setLoading(true)
      const response = await authService.register(data)

      if (response.success && response.user) {
        setUser(response.user)
        return { success: true }
      } else {
        return {
          success: false,
          error: response.error?.message || 'Erreur lors de l\'inscription'
        }
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Erreur inattendue lors de l\'inscription'
      }
    } finally {
      setLoading(false)
    }
  }

  const logout = async (): Promise<void> => {
    try {
      setLoading(true)
      await authService.logout()
      setUser(null)
    } catch (error) {
      console.error('Logout error:', error)
      // Force logout even if API call fails
      localStorage.removeItem('authToken')
      localStorage.removeItem('userData')
      setUser(null)
    } finally {
      setLoading(false)
    }
  }

  const updateUser = (userData: Partial<User>): void => {
    if (user) {
      const updatedUser = { ...user, ...userData }
      setUser(updatedUser)
      authService.updateCurrentUser(userData)
    }
  }

  const value: AuthContextType = {
    user,
    loading,
    login,
    register,
    logout,
    signOutUser: logout, // Alias pour la compatibilité avec le code existant
    updateUser,
    isAuthenticated: authService.isAuthenticated()
  }

  return React.createElement(
    AuthContext.Provider,
    { value },
    children
  )
}